/** Status dot colors for the action bar. */
export const TOOL_STATUS_DOT = {
    empty: 'bg-[#A8A099]',
    ready: 'bg-pdf-cat-solid',
    processing: 'bg-primary',
    success: 'bg-[#1E9E5E]',
    error: 'bg-[#D33A2C]',
};

/**
 * Derive the tool-page status badge from common tool state.
 * @param {{ hasInput?: boolean, canRun?: boolean, processing?: boolean, hasResult?: boolean, hasError?: boolean }} state
 * @returns {'empty' | 'ready' | 'processing' | 'success' | 'error'}
 */
export function deriveToolStatus({ hasInput, canRun, processing, hasResult, hasError }) {
    if (processing) return 'processing';
    if (hasError) return 'error';
    if (hasResult) return 'success';
    if (canRun || hasInput) return 'ready';
    return 'empty';
}

export function genericHowToSteps(t) {
    return [1, 2, 3, 4].map((i) => t(`toolPage.genericSteps.step${i}`));
}

/** Map tool slug to i18n root key for subtitle/description. */
const SLUG_I18N_KEYS = {
    'result/education-board-result': 'educationBoardResult',
    'result/national-university-result': 'nationalUniversityResult',
    'questions-create': 'questionsCreate',
    'image/compress': 'compressImage',
    'image/document-cleanup': 'documentCleanupImage',
    'image/nid-to-pdf': 'nidToPdf',
    'image/remove-background': 'removeBackground',
    'image/govt-job-photo-sign': 'govtJobPhotoSign',
    'image/passport-to-pdf': 'passportToPdf',
    'image/convert': 'convertImage',
    'image/qr-code': 'qrCode',
    'image/resize': 'resizeImage',
    'image/crop': 'cropImage',
    'image/to-pdf': 'toPdfImage',
    'image/watermark': 'watermarkImage',
    'image/flatten': 'flattenImage',
    'image/barcode': 'barcodeImage',
    'image/color-picker': 'colorPickerImage',
    'image/signature-pad': 'signaturePadImage',
    'image/signature-from-text': 'signatureFromTextImage',
    'image/logo-color-changer': 'logoColorChangerImage',
    'image/compress-gif': 'compressGifImage',
    'image/upscale': 'upscaleImage',
    'image/notice-generator': 'noticeGeneratorImage',
    'pdf/compress': 'compressPdf',
    'pdf/merge': 'mergePdf',
    'pdf/create': 'createPdf',
    'pdf/split': 'splitPdf',
    'pdf/pdf-to-image': 'pdfToImage',
    'pdf/protect': 'protectPdf',
    'pdf/rotate': 'rotatePdf',
    'pdf/remove-pages': 'removePagesPdf',
    'pdf/watermark': 'watermarkPdf',
    'pdf/extract-images': 'extractImagesPdf',
    'pdf/permissions': 'permissionsPdf',
    'pdf/resize': 'resizePdf',
    'pdf/crop': 'cropPdf',
    'pdf/pdf-to-excel': 'pdfToExcel',
    'pdf/repair': 'repairPdf',
    'pdf/edit': 'editPdf',
    'text/bijoy-unicode': 'bijoyUnicodeText',
    'text/banglish-typing': 'banglishTypingText',
    'text/number-to-words': 'numberToWordsText',
    'text/image-to-text': 'imageToText',
    'text/text-analyzer': 'textAnalyzerText',
    'text/date-format': 'dateFormatText',
    'video/compress': 'compressVideo',
    'video/trim': 'trimVideo',
    'video/merge': 'mergeVideo',
    'video/convert': 'convertVideo',
    'video/watermark': 'watermarkVideo',
    'video/volume': 'volumeVideo',
    'video/speed': 'speedVideo',
    'video/to-gif': 'toGifVideo',
    'video/rotate': 'rotateVideo',
    'video/remove-audio': 'removeAudioVideo',
    'general/family-card-form': 'familyCardForm',
    'general/voter-migration-form': 'voterMigrationForm',
    'general/nid-information-correction-form': 'nidCorrectionForm',
    'general/special-information-form': 'specialInformationForm',
    'general/voter-registration-form': 'voterRegistrationForm',
    'general/voter-inclusion-form': 'voterInclusionForm',
    'general/embassy-birth-registration-form': 'embassyBirthForm',
    'general/udyog-application-form': 'udyogForm',
    'general/bhata-tracking': 'bhataTracking',
    'general/fuel-card-form': 'fuelCardForm',
    'general/ats-cv-maker': 'atsCvMaker',
    'general/age-calculator': 'ageCalculator',
    'general/emi-calculator': 'emiCalculator',
    'general/bmi-calculator': 'bmiCalculator',
    'general/typing-practice': 'typingPractice',
    'general/invoice-designer': 'invoiceDesigner',
    'general/bangladesh-travel-map': 'bdTravelMap',
};

export function getToolSubtitleKey(slug) {
    const root = SLUG_I18N_KEYS[slug];
    return root ? `${root}.subtitle` : null;
}

export function downloadBlob(blob, name) {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = name;
    anchor.rel = 'noopener';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
}

const STATUS_TEXT_KEYS = {
    empty: 'toolPage.statusText.idle',
    ready: 'toolPage.statusText.ready',
    processing: 'toolPage.statusText.processing',
    success: 'toolPage.statusText.success',
    error: 'toolPage.statusText.error',
};

/** Status line shown above the trust banner. */
export function getToolStatusText(status, t, overrides = {}) {
    if (overrides[status]) return overrides[status];
    const key = STATUS_TEXT_KEYS[status];
    return key ? t(key) : '';
}

/** Stable unique id for sortable tool file rows (works on HTTP / older browsers). */
export function createToolItemId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/** Copy text in the current user-gesture context (sync fallback for non-HTTPS). */
export function copyTextToClipboard(text) {
    try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        ta.setSelectionRange(0, text.length);
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        return ok;
    } catch {
        return false;
    }
}
